import { downloadFile, requestJson } from "@/lib/api";

export type OperationalCostMode = "FIXED" | "PERCENT";

/** Prévia somente-leitura do custo parcial, calculada pelo backend (precisão interna preservada). */
export interface PartialCostPreview {
  rawCost: number | null;
  rawCostUnitName: string | null;
  mermaPercentApplied: number;
  mermaDefault: boolean;
  mermaComment: string | null;
  mermaSurcharge: number | null;
  operationalMode: OperationalCostMode | null;
  operationalCost: number | null;
  operationalUnitName: string | null;
  partialCost: number | null;
  complete: boolean;
  incompleteReasons: string[];
}

export interface ProductCostDetail {
  productId: string;
  code: number | null;
  name: string;
  active: boolean;
  rawCostPrice: number | null;
  rawCostUnitId: string | null;
  rawCostUnitName: string | null;
  rawCostReferenceDate: string | null;
  rawCostNote: string | null;
  operationalMode: OperationalCostMode | null;
  operationalValue: number | null;
  operationalPercent: number | null;
  operationalUnitId: string | null;
  operationalUnitName: string | null;
  operationalNote: string | null;
  version: number | null;
  hasCost: boolean;
  updatedAt: string | null;
  /** Processado cuja matéria-prima é herdada da origem IN NATURA (campos próprios ficam read-only). */
  inheritsRawCost: boolean;
  /** Origem editável pelo seletor simples (processado ativo, 0/1 vínculo somente de custo). */
  originEditable: boolean;
  originProductId: string | null;
  originProductCode: number | null;
  originProductName: string | null;
  inheritedRawCostReferenceDate: string | null;
  preview: PartialCostPreview;
  packagingByPresentation: PackagingPresentation[];
}

export type PackagingCompositionState = "NAO_CADASTRADA" | "SEM_EMBALAGEM" | "COM_EMBALAGENS";

export interface PackagingComponentView {
  materialId: string;
  materialName: string;
  materialUnit: string | null;
  materialUnitLabel: string | null;
  quantity: number;
  materialCost: number | null;
  lineCost: number | null;
  materialActive: boolean;
  materialHasCost: boolean;
}

export interface PackagingPresentation {
  unitId: string;
  unitName: string;
  state: PackagingCompositionState;
  version: number | null;
  components: PackagingComponentView[];
  packagingCost: number | null;
  packagingComplete: boolean;
  packagingReasons: string[];
  partialWithPackaging: number | null;
  partialComplete: boolean;
  partialReasons: string[];
}

export interface PackagingMaterialOption {
  id: string;
  name: string;
  materialUnit: string;
  materialUnitLabel: string;
  currentCost: number | null;
  active: boolean;
}

export interface ProductPackagingHistoryEntry {
  id: string;
  changedAt: string;
  changedBy: string;
  changedFields: string[];
  oldValues: string;
  newValues: string;
  note: string | null;
}

/** Rótulos das razões de composição/parcial de embalagem (espelham os códigos do backend). */
export const PACKAGING_REASON_LABELS: Record<string, string> = {
  COMPOSICAO_NAO_CADASTRADA: "Composição de embalagem não cadastrada",
  MATERIAL_SEM_CUSTO: "Material de embalagem sem custo cadastrado",
  MATERIAL_INATIVO: "Material de embalagem inativo (substituir)",
  CUSTO_BASE_INCOMPLETO: "Custo base incompleto",
  EMBALAGEM_INCOMPLETA: "Composição de embalagem incompleta",
  UNIDADES_INCOMPATIVEIS: "Apresentação difere da unidade de referência do custo base (sem conversão)",
};

/** Resumo por apresentação (unidade) do custo com embalagem — enriquecido na listagem pelo backend. */
export interface ProductCostPresentationSummary {
  unitId: string;
  unitName: string;
  packagingCost: number | null;
  packagingComplete: boolean;
  totalWithPackaging: number | null;
  totalComplete: boolean;
  totalReasons: string[];
}

export interface ProductCostListItem {
  productId: string;
  code: number | null;
  name: string;
  active: boolean;
  rawCost: number | null;
  rawCostUnitName: string | null;
  operationalMode: OperationalCostMode | null;
  operationalCost: number | null;
  /** Percentual bruto no modo PERCENT (null em FIXED/não cadastrado). */
  operationalPercent: number | null;
  mermaUsedPercent: number;
  mermaDefault: boolean;
  referenceDate: string | null;
  complete: boolean;
  hasCost: boolean;
  updatedAt: string | null;
  inheritsRawCost: boolean;
  originProductName: string | null;
  /** Acréscimo por merma (R$) na unidade de referência do custo base. */
  mermaSurcharge: number | null;
  /** Custo base (matéria-prima + merma + operacional) na unidade de referência; null = incompleto. */
  baseCost: number | null;
  /** Razões de incompletude do custo base (códigos; ver INCOMPLETE_REASON_LABELS). */
  baseReasons: string[];
  /** Custo com embalagem por apresentação (uma linha por unidade/apresentação). */
  presentations: ProductCostPresentationSummary[];
}

export interface ProductCostHistoryEntry {
  id: string;
  changedAt: string;
  changedBy: string;
  origin: string;
  changedFields: string[];
  oldValues: string;
  newValues: string;
  referenceDate: string | null;
  operationalMode: string | null;
  rawCostUnitName: string | null;
  operationalUnitName: string | null;
  note: string | null;
  /** Referência da importação XLSX (origem XLSX); null nas demais. */
  importId: string | null;
  /** Referência da aplicação de custo de NF (origem NF_FORNECEDOR); null nas demais e em registros antigos. */
  costApplicationId: string | null;
  /** Nome do emitente declarado no XML da NF (origem NF_FORNECEDOR); null em registros antigos/sem backfill. */
  nfSupplierName: string | null;
  /** Chave de acesso da NF-e para abrir o DANFE gerado do XML (origem NF_FORNECEDOR); null nas demais. */
  nfAccessKey: string | null;
  /** Número da NF (origem NF_FORNECEDOR); null nas demais. */
  nfNumber: string | null;
}

export interface ProductCostUpsertPayload {
  rawCostPrice: number | null;
  rawCostUnitId: string | null;
  rawCostReferenceDate: string | null;
  rawCostNote: string | null;
  operationalMode: OperationalCostMode | null;
  operationalValue: number | null;
  operationalPercent: number | null;
  operationalUnitId: string | null;
  operationalNote: string | null;
  /** Versão lida (lock otimista). Null na primeira gravação; obrigatória na edição. */
  version: number | null;
}

/** Rótulos das razões de composição incompleta (espelham os códigos do backend). */
export const INCOMPLETE_REASON_LABELS: Record<string, string> = {
  MATERIA_PRIMA_AUSENTE: "Custo da matéria-prima não cadastrado",
  OPERACIONAL_AUSENTE: "Custo operacional não configurado",
  OPERACIONAL_SEM_VALOR: "Modalidade operacional sem valor informado",
  UNIDADES_INCOMPATIVEIS: "Unidade do custo operacional difere da matéria-prima (sem conversão)",
  ORIGEM_AUSENTE: "Produto processado sem matéria-prima de origem vinculada",
  ORIGEM_MULTIPLA: "Produto com múltiplas matérias-primas (herança de custo não definida nesta etapa)",
  ORIGEM_INATIVA: "Matéria-prima de origem inativa",
  ORIGEM_SEM_CUSTO: "Matéria-prima de origem sem custo cadastrado",
  ORIGEM_INVALIDA: "Vínculo de matéria-prima de origem inválido",
};

export async function fetchProductCosts(): Promise<ProductCostListItem[]> {
  return requestJson<ProductCostListItem[]>("/api/v1/admin/product-costs");
}

/**
 * Exporta a consulta de custos em XLSX, espelhando os filtros atuais (busca + somente incompletos). O
 * backend cobre TODAS as linhas do resultado e respeita a permissão (CATALOGO.CUSTO.VISUALIZAR).
 */
export async function exportProductCostsXlsx(query: string, onlyIncomplete: boolean): Promise<void> {
  const params = new URLSearchParams();
  if (query.trim()) params.set("query", query.trim());
  if (onlyIncomplete) params.set("onlyIncomplete", "true");
  const qs = params.toString();
  return downloadFile(
    `/api/v1/admin/product-costs/export.xlsx${qs ? `?${qs}` : ""}`,
    "custos_produtos.xlsx",
  );
}

export async function fetchProductCost(productId: string): Promise<ProductCostDetail> {
  return requestJson<ProductCostDetail>(`/api/v1/admin/product-costs/${productId}`);
}

export async function fetchProductCostHistory(productId: string): Promise<ProductCostHistoryEntry[]> {
  return requestJson<ProductCostHistoryEntry[]>(`/api/v1/admin/product-costs/${productId}/history`);
}

export async function updateProductCost(
  productId: string,
  payload: ProductCostUpsertPayload,
): Promise<ProductCostDetail> {
  return requestJson<ProductCostDetail>(`/api/v1/admin/product-costs/${productId}`, {
    method: "PUT",
    body: payload,
  });
}

/**
 * Cadastra/troca/remove a matéria-prima de origem (vínculo somente de custo) de um processado.
 * `originProductId` null = remover. `expectedOriginProductId` = origem vista por último (lock otimista).
 */
export async function updateProductCostOrigin(
  productId: string,
  originProductId: string | null,
  expectedOriginProductId: string | null,
): Promise<ProductCostDetail> {
  return requestJson<ProductCostDetail>(`/api/v1/admin/product-costs/${productId}/origin`, {
    method: "PUT",
    body: { originProductId, expectedOriginProductId },
  });
}

/** Materiais ativos para seleção na composição (protegido por CATALOGO.CUSTO.*, sem gestão de Suprimentos). */
export async function fetchPackagingMaterialOptions(): Promise<PackagingMaterialOption[]> {
  return requestJson<PackagingMaterialOption[]>("/api/v1/admin/product-costs/material-options");
}

export async function upsertProductPackaging(
  productId: string,
  unitId: string,
  payload: { state: PackagingCompositionState; components: { materialId: string; quantity: number }[]; version: number | null },
): Promise<ProductCostDetail> {
  return requestJson<ProductCostDetail>(`/api/v1/admin/product-costs/${productId}/packaging/${unitId}`, {
    method: "PUT",
    body: payload,
  });
}

/** Limpa a composição de uma apresentação (volta a "Não cadastrada"), mantendo o registro e avançando a versão. */
export async function clearProductPackaging(
  productId: string,
  unitId: string,
  version: number | null,
): Promise<ProductCostDetail> {
  return requestJson<ProductCostDetail>(`/api/v1/admin/product-costs/${productId}/packaging/${unitId}/clear`, {
    method: "PUT",
    body: { version },
  });
}

export async function fetchProductPackagingHistory(
  productId: string,
  unitId: string,
): Promise<ProductPackagingHistoryEntry[]> {
  return requestJson<ProductPackagingHistoryEntry[]>(
    `/api/v1/admin/product-costs/${productId}/packaging/${unitId}/history`,
  );
}
